/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatUTCDate } from "../../utils/utils";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import UserDetailLink from "../common/UserDetailLink";
import WorkoutDetailSummaryCards from "./WorkoutDetailSummaryCards";
import "../active-users/active-users.css";
import "./WorkoutData.css";

const LIMIT = 20;

// Milliseconds → human-readable h/m/s (preserved from the original detail page).
const formatDuration = (ms) => {
  if (ms == null) return '-';
  const totalSeconds = Math.floor(Number(ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
};

const EXPORT_COLUMNS = [
  { key: 'workout_type', label: 'Workout Type' },
  { key: 'workout_type_raw', label: 'Workout Type Raw' },
  { key: 'source_name', label: 'Source Name' },
  { key: 'duration', label: 'Duration' },
  { key: 'start_time', label: 'Start Time' },
  { key: 'end_time', label: 'End Time' },
  { key: 'timestamp_utc', label: 'Timestamp UTC' },
];

const WorkoutDetailList = () => {
  const navigate = useNavigate();
  const { encodedUserId } = useParams();
  const decodedUserId = encodedUserId ? atob(encodedUserId) : '';

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [records, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [sortField, setSortField] = useState('timestamp_utc');
  const [sortOrder, setSortOrder] = useState('DESC');

  const [summaryCards, setSummaryCards] = useState({ user: null, total_records: 0, distinct_types: 0, total_duration_ms: 0, avg_duration_ms: null });

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, sortField, sortOrder);
  }, [token]);

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const listQs = (p, f, t, sf, so) => {
    const qp = new URLSearchParams({
      page: p, limit: LIMIT, from_date: f, to_date: t,
      ...(decodedUserId && { user_id: decodedUserId }),
      ...(sf && { sortField: sf }), ...(so && { sortOrder: so }),
    });
    return qp.toString();
  };
  const summaryQs = (f, t) => `user_id=${decodedUserId}&from_date=${f}&to_date=${t}`;
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, sf, so) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/workout-details?${listQs(p, f, t, sf, so)}`, auth());
      setData(res.data.data || []);
      setTotalItem(res.data.totalItem || 0);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, sf, so) => {
    setLoading(true); setError('');
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/workout-details?${listQs(p, f, t, sf, so)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/workout-detail-summary?${summaryQs(f, t)}`, auth()),
      ]);
      setData(list.data.data || []);
      setTotalItem(list.data.totalItem || 0);
      setSummaryCards(sum.data.data || { user: null, total_records: 0, distinct_types: 0, total_duration_ms: 0, avg_duration_ms: null });
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, sortField, sortOrder);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, sortField, sortOrder);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, sortField, sortOrder); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setPreset(null); setSortField('timestamp_utc'); setSortOrder('DESC'); setPage(1);
    loadAll(1, '', '', 'timestamp_utc', 'DESC');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, field, order);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/workout-detail-export?user_id=${decodedUserId}&from_date=${fromDate}&to_date=${toDate}&sortField=${sortField}&sortOrder=${sortOrder}`,
      auth());
    return (res.data.data || []).map((r) => ({
      ...r,
      duration: formatDuration(r.duration),
    }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const hasRange = !!(fromDate || toDate);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const userName = summaryCards.user?.name || '';

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Workout sessions › <b>Details</b></span>
            <span className="au-route">/workout-details</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/workout-sessions">← Back</Link></span>
          </div>
          <h1 className="au-title">Workout Details{userName ? <span className="au-subtitle"> — {userName}</span> : null}</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the workout details. Please try again." onRetry={() => loadAll(page, fromDate, toDate, sortField, sortOrder)} />
          ) : (
            <>
              {/* Summary before detail */}
              <WorkoutDetailSummaryCards
                totalRecords={summaryCards.total_records}
                distinctTypes={summaryCards.distinct_types}
                totalDurationMs={summaryCards.total_duration_ms}
                avgDurationMs={summaryCards.avg_duration_ms}
              />

              <DateRangeBar
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="wk-detail-range-note">
                The date range filters workouts by their <b>record date (UTC)</b> — the value shown as “Timestamp UTC”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all workouts (no date filter).'}
              </p>

              {/* Detail table */}
              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : records.length === 0 ? (
                <EmptyState title="No workouts in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'This user has no workout records for the current filters.' : 'This user has no workout records to show yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Workout records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="workout-details.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table wk-table" data-testid="wk-detail-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th>
                          <th>Workout Type</th><th>Workout Type Raw</th><th>Source Name</th>
                          <th>Duration</th><th>Start Time</th><th>End Time</th>
                          <SortTh field="timestamp_utc" label="Timestamp UTC" />
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row, index) => (
                          <tr key={row.id ?? index}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis"><UserDetailLink userId={decodedUserId}>{row.name}</UserDetailLink></td>
                            <td data-label="Email" className="au-ellipsis"><UserDetailLink userId={decodedUserId}>{row.email}</UserDetailLink></td>
                            <td data-label="Workout Type">{row.workout_type || '-'}</td>
                            <td data-label="Workout Type Raw">{row.workout_type_raw || '-'}</td>
                            <td data-label="Source Name">{row.source_name || '-'}</td>
                            <td data-label="Duration" className="tnum">{formatDuration(row.duration)}</td>
                            <td data-label="Start Time">{row.start_time ? formatUTCDate(row.start_time) : '-'}</td>
                            <td data-label="End Time">{row.end_time ? formatUTCDate(row.end_time) : '-'}</td>
                            <td data-label="Timestamp UTC">{row.timestamp_utc ? formatUTCDate(row.timestamp_utc) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkoutDetailList;
